import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  setBasicInfo,
  getBasicInfo,
  getSessionStorageItem,
  setSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_EMAIL_CAPTURE, 
  SESSION_STORAGE_SAVED_QUOTE_DATA
} from '../../../../../sessionStorage';

import AboutData from './AboutData';
import FaqData from './FaqData';
import PolicyCostData from './PolicyCostData';
import PurchasingProcessData from './PurchasingProcessData';
import ProductCarriers from './ProductCarriers';
import TermLengths from '../../../Lists/CoverageLengths/AmericoCoverageLengths';
import LoanProtectionArticleData from './LoanProtectionArticleData';
import AboutMeForm from '../../../../Quoters/AmericoLoanProtection/steps/AboutMeForm';
import SaveQuotePartnerReferral from '../../../../Quoters/CustomizeTerm/SaveTermQuotePartnerReferral/SaveQuotePartnerReferral';
import { getPrimaryColor } from '../../../../Quoters/Common/Helpers/Utility';

export default function LoanProtectionData(props) {
  const primaryBrandColor = getPrimaryColor();
  const minAge = 20;
  const maxAge = 65;
  const minCoverageAmount = 25000;
  const maxCoverageAmount = 1000000;
  
  const navigate = useNavigate();
  const location = useLocation();
  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

  const accountProductTypeSettings = props ? accountObj.accountProducts.find(product => product.productLandingUrl === location.pathname) : null;
  const accountLandingOverride = accountObj.accountOverrides && accountProductTypeSettings ?
    accountObj.accountOverrides.find(override => override.accountProductTypeId === accountProductTypeSettings.productTypeId) : null;
  
  const basicInfo = getBasicInfo();
  const emailCapture = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);

  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);
  const [errorMessage, setErrorMessage] = useState({ message: '', status: '' });

  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);

  const [emailCaptureActive, setEmailCaptureActive] = useState(false);
  const [triggerNext, setTriggerNext] = useState(false);
  const [enableInitialEmailCapture, setEnableInitialEmailCapture] = useState(accountObj.account.enableInitialEmailCapture);
  const [enableTimeoutEmailCapture, setEnableTimeoutEmailCapture] = useState(accountObj.account.enableTimeoutEmailCapture);
  const [initialEmailCaptureDisplayed, setInitialEmailCaptureDisplayed] = useState(emailCapture.initialEmailCaptureDisplayed || false);
  const [timeoutEmailCaptureDisplayed, setTimeoutEmailCaptureDisplayed] = useState(emailCapture.timeoutEmailCaptureDisplayed || false);
  const [enablePartnerReferralEmailCapture, setEnablePartnerReferralEmailCapture] = useState(emailCapture.enablePartnerReferralEmailCapture || false);
  const [isPartnerReferralEmailCaptureActive, setIsPartnerReferralEmailCaptureActive] = useState(emailCapture.isPartnerReferralEmailCaptureActive || false);
  const [modalSaveQuoteIsOpen, setModalSaveQuoteIsOpen] = useState(false);

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };

  const setEmailPrefill = () => {
    setBasicInfo(basicInfo);
  };

  const handleContactChange = (contact) => {
    setSaveQuoteContact(contact);
  };

  const setTimeoutEmailDisplayed = () => {
    setTimeoutEmailCaptureDisplayed(true);
  };

  const setInitialEmailDisplayed = () => {
    setInitialEmailCaptureDisplayed(true);
  };

  const onSubmit = () => {
    setEmailCaptureActive(false);
    if (props.aboutMeFormRef.current) {
      props.aboutMeFormRef.current.onSubmit();
    }
  };

  useEffect(() => {
    if (triggerNext && !emailCaptureActive) {
      navigate('/loan-protection/quote-results');
    }
  }, [triggerNext, emailCaptureActive]);

  useEffect(() => {
    if (saveQuoteContact.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact.firstName,
        "lastName": saveQuoteContact.lastName,
        "emailAddress": saveQuoteContact.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  const LoanProtectionForm = (<AboutMeForm
    ageSpecs={{ min: minAge, max: maxAge }}
    onNext={() => {
      setTriggerNext(true);
    }}
    brandTheme={primaryBrandColor}
    showHeader={false}
    submitButtonVariant={'landing'}
    showTestimonialPanel={true}
    isLoanProtection={props ? props.isLoanProtection : true}
    unavailableStates={["NY", "VI"]}
    ref={props.aboutMeFormRef}
    setModalSaveQuoteIsOpen={setModalSaveQuoteIsOpen}
    setSaveQuoteData={setSaveQuoteData}
    saveQuoteContact={saveQuoteContact}
    emailCapture={emailCapture}
    setEmailCaptureActive={setEmailCaptureActive}
  />);

  const defaultValueProposition = [{
    title: " Loan Protection Life Insurance",
    subtitle: "Protect Your Mortgage or Business Loan",
    subtitleExtended: "LoanMatch Protector is the easiest way to secure a term policy to cover a specific financial obligation."
  }];

  return {
    name: 'Loan Protection Life Insurance',
    subtitle: 'Protect Your Mortgage or Business Loan',
    footerMessage: "Get decreasing term life insurance from start to finish completely online.",
    heroText: accountLandingOverride ? JSON.parse(accountLandingOverride.valueProposition) : defaultValueProposition,
    basicInfoForm: () => {
      return LoanProtectionForm;
    },
    about: AboutData(minAge, maxAge, minCoverageAmount, maxCoverageAmount, TermLengths()),
    purchasingProcess: PurchasingProcessData(),
    policyCost: PolicyCostData(),
    faq: FaqData(),
    termArticle: LoanProtectionArticleData(),
    productCarriers: ProductCarriers,
    isAddDefaultFaq: false,
    saveMyQuotePartnerReferral: <SaveQuotePartnerReferral
      type='landing-screen'
      title="Almost there!"
      setQuoteSaved={setQuoteSaved}
      isQuoteSaved={isQuoteSaved}
      setEmailPrefill={(e) => setEmailPrefill(e)}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      setEmailModal={{
        enableInitialEmailCapture: enableInitialEmailCapture,
        enableTimeoutEmailCapture: enableTimeoutEmailCapture,
        initialEmailCaptureDisplayed: initialEmailCaptureDisplayed,
        timeoutEmailCaptureDisplayed: timeoutEmailCaptureDisplayed,
        isPartnerReferralEmailCaptureActive: isPartnerReferralEmailCaptureActive,
        enablePartnerReferralEmailCapture: enablePartnerReferralEmailCapture,
      }}
      modalSaveQuoteIsOpen={modalSaveQuoteIsOpen}
      setModalSaveQuoteIsOpen={setModalSaveQuoteIsOpen}
      setTimeoutEmailDisplayed={() => setTimeoutEmailDisplayed()}
      setInitialEmailDisplayed={() => setInitialEmailDisplayed()}
      saveQuoteData={saveQuoteData}
      handleContactChange={handleContactChange}
      onSubmit={onSubmit}
    />,
  };
}
